import React, { memo, useCallback, useRef, useState } from 'react';

interface SliderProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  showValue?: boolean;
  valueFormatter?: (v: number) => string;
  valueParser?: (v: string) => number;
  accentColor?: string;
}

export const Slider = memo<SliderProps>(({
  value,
  min,
  max,
  step = 1,
  onChange,
  disabled = false,
  showValue = true,
  valueFormatter = (v) => String(v),
  valueParser = (v) => parseFloat(v),
  accentColor = '#06b6d4',
}) => {
  const percentage = ((value - min) / (max - min)) * 100;
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  // 按住拖动时切换「抓住」光标（ref 不触发重渲染，光标切换需要 state）
  const [grabbing, setGrabbing] = useState(false);

  const startEdit = () => {
    setEditText(valueFormatter(value));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commitEdit = () => {
    const parsed = valueParser(editText);
    if (Number.isFinite(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      onChange(clamped);
    }
    setEditing(false);
  };

  // 指针驱动取值：getBoundingClientRect 自带 transform 感知，InverseScaleBox 缩放容器下也能按光标实际位置实时跟手
  const commitFromPointer = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const stepped = Math.round((min + ratio * (max - min)) / step) * step;
    onChange(Math.min(max, Math.max(min, Number(stepped.toFixed(6)))));
  }, [min, max, step, onChange]);

  const handleTrackPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = true;
    setGrabbing(true);
    commitFromPointer(e.clientX);
  }, [disabled, commitFromPointer]);

  const handleTrackPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    commitFromPointer(e.clientX);
  }, [commitFromPointer]);

  const handleTrackPointerEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    setGrabbing(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  return (
    <div className="flex items-center gap-3">
      <div
        ref={trackRef}
        onPointerDown={handleTrackPointerDown}
        onPointerMove={handleTrackPointerMove}
        onPointerUp={handleTrackPointerEnd}
        onPointerCancel={handleTrackPointerEnd}
        className={`relative flex-1 h-4 flex items-center touch-none ${disabled ? 'cursor-not-allowed' : grabbing ? 'cursor-grabbing' : 'cursor-grab'}`}
      >
        <div className="w-full h-1 bg-[#3e3e44] rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: accentColor }} />
        </div>
        <div
          className="absolute w-3 h-3 bg-white rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.3)] pointer-events-none"
          style={{ left: `calc(${percentage}% - 6px)` }}
        />
      </div>
      {showValue && (
        <div className="relative shrink-0 min-w-[52px] h-6 px-2 flex items-center justify-center bg-[#3e3e44] rounded-md">
          {editing ? (
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitEdit();
                else if (e.key === 'Escape') setEditing(false);
              }}
              className="absolute inset-0 bg-transparent text-[11px] text-zinc-200 outline-none font-mono tabular-nums text-center"
            />
          ) : (
            <span
              className="text-[11px] text-zinc-300 font-mono tabular-nums cursor-text"
              onDoubleClick={startEdit}
              onClick={startEdit}
            >
              {valueFormatter(value)}
            </span>
          )}
        </div>
      )}
    </div>
  );
});
